import type {
  CreateOpportunityCommentRequest,
  OpportunityComment,
  OpportunityCommentsResponse,
  UpdateOpportunityCommentRequest,
} from 'backend/types'
import { apiFetch } from '..'

/**
 * コメントマーカーレスポンス型
 */
export interface CommentMarkersResponse {
  commentNumber: number | null
  count: number
}

/**
 * コメント一覧取得API
 *
 * @param opportunityId - オポチュニティID
 * @param params - クエリパラメータ
 * @param params.offset - オフセット（デフォルト: 0）
 * @param params.limit - 上限（オプション）
 * @returns コメント一覧レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function getOpportunityComments(
  opportunityId: number,
  params?: {
    offset?: number
    limit?: number
  },
): Promise<OpportunityCommentsResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/opportunity/${opportunityId}/comments${queryString ? `?${queryString}` : ''}`

  return apiFetch<OpportunityCommentsResponse>(url, { method: 'GET' })
}

/**
 * コメント作成API
 *
 * @param opportunityId - オポチュニティID
 * @param data - コメント作成データ
 * @returns 作成されたコメント
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function createOpportunityComment(
  opportunityId: number,
  data: CreateOpportunityCommentRequest,
): Promise<OpportunityComment> {
  return apiFetch<OpportunityComment>(`/api/opportunity/${opportunityId}/comment`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * コメント更新API
 *
 * @param opportunityId - オポチュニティID
 * @param commentId - コメントID
 * @param data - コメント更新データ
 * @returns 更新されたコメント
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function updateOpportunityComment(
  opportunityId: number,
  commentId: number,
  data: UpdateOpportunityCommentRequest,
): Promise<OpportunityComment> {
  return apiFetch<OpportunityComment>(`/api/opportunity/${opportunityId}/comment/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * コメント削除API
 *
 * @param opportunityId - オポチュニティID
 * @param commentId - コメントID
 * @returns 削除されたコメント（論理削除）
 * @throws ApiError - 認証失敗（403）、権限不足（403）、サーバーエラー（500）
 */
export async function deleteOpportunityComment(opportunityId: number, commentId: number): Promise<OpportunityComment> {
  return apiFetch(`/api/opportunity/${opportunityId}/comment/${commentId}`, {
    method: 'DELETE',
  })
}

/**
 * コメントマーカー取得API（未読コメント数）
 *
 * @param opportunityId - オポチュニティID
 * @returns コメントマーカーレスポンス
 * @throws ApiError
 */
export async function getCommentMarkers(opportunityId: number): Promise<CommentMarkersResponse> {
  return apiFetch<CommentMarkersResponse>(`/api/opportunity/${opportunityId}/comment/markers`, {
    method: 'GET',
  })
}
